import React, { useCallback, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import AthletePickerModal from './modals/AthletePickerModal';
import CreateEditEntryModal from './modals/CreateEditEntryModal';
import FilterOptionsModal from './modals/FilterOptionsModal';
import GroupPickerModal from './modals/GroupPickerModal';
import ImageLightboxModal from './modals/ImageLightboxModal';
import JournalModalWrapper from './modals/JournalModalWrapper';
import MediaModalContent from './modals/MediaModalContent';
import ReportPostModal from './modals/ReportPostModal';
import MediaModalWrapper from './modals/MediaModalWrapper';

import {
  clearRemoveMediaArray,
  resetEntryError,
  resetSubmitSupportError,
  setActiveReduxModal,
  setEditJournalEntry,
  setReportPostId,
  setTempExercise,
  setUploadMediaFalse,
} from './ducks/journalActions';

const MODAL_CONFIG = {
  'athlete-picker-modal': {
    Modal: AthletePickerModal,
    Wrapper: JournalModalWrapper,
    wrapperProps: {},
  },
  'create-entry-modal': {
    Modal: CreateEditEntryModal,
    Wrapper: JournalModalWrapper,
    wrapperProps: { marginTop: '80px' },
  },
  'edit-entry-modal': {
    Modal: CreateEditEntryModal,
    Wrapper: JournalModalWrapper,
    wrapperProps: { marginTop: '80px' },
  },
  'filter-options-modal': {
    Modal: FilterOptionsModal,
    Wrapper: JournalModalWrapper,
    wrapperProps: {},
  },
  'group-picker-modal': {
    Modal: GroupPickerModal,
    Wrapper: JournalModalWrapper,
    wrapperProps: {},
  },
  'image-lightbox': {
    Modal: ImageLightboxModal,
    Wrapper: MediaModalWrapper,
    wrapperProps: {},
  },
  'play-message-video-modal': {
    Modal: MediaModalContent,
    Wrapper: MediaModalWrapper,
    wrapperProps: {},
  },
  'report-post-modal': {
    Modal: ReportPostModal,
    Wrapper: JournalModalWrapper,
    wrapperProps: { customPadding: '30px', width: '380px' },
  },
};

const MainContainer = styled('main')`
  max-height: calc(100vh - 210px);
  overflow: auto;
`;

const ModalHandler = ({ scrollToTop }) => {
  const { activeModal, messageMediaUrl } = useSelector((state) => state.journal.data);
  const dispatch = useDispatch();

  const altMediaUrl = useMemo(() => {
    if (messageMediaUrl.includes('drive.google')) {
      const split = messageMediaUrl.split('/');
      const id = split[split.length - 2];
      return `https://drive.google.com/uc?export=download&id=${id}`;
    }
    return messageMediaUrl;
  }, [messageMediaUrl]);

  const closeModal = useCallback(() => {
    batch(() => {
      dispatch(setActiveReduxModal(''));
      dispatch(setTempExercise({}));
      dispatch(setEditJournalEntry({}));
      dispatch(setUploadMediaFalse());
      dispatch(clearRemoveMediaArray());
      dispatch(setReportPostId(0));
      dispatch(resetEntryError());
      dispatch(resetSubmitSupportError());
    });
  }, [dispatch]);

  const modalHandler = useCallback(() => {
    const config = MODAL_CONFIG[activeModal];
    if (!config) return null;

    const { Modal, Wrapper, wrapperProps } = config;

    const modalProps = { onRequestClose: closeModal };

    if (activeModal === 'athlete-picker-modal' || activeModal === 'group-picker-modal' || activeModal === 'create-entry-modal') {
      modalProps.scrollToTop = scrollToTop;
    }

    // Add mediaUrl for media modals
    if (activeModal === 'play-message-video-modal' || activeModal === 'image-lightbox') {
      modalProps.mediaUrl = altMediaUrl;
    }

    return (
      <Wrapper isOpen onRequestClose={closeModal} {...wrapperProps}>
        <Modal {...modalProps} />
      </Wrapper>
    );
  }, [activeModal, altMediaUrl, closeModal, scrollToTop]);

  return <MainContainer>{modalHandler()}</MainContainer>;
};

ModalHandler.propTypes = {
  scrollToTop: PropTypes.func.isRequired,
};

export default ModalHandler;
